import type { PiTier } from "./api";

/**
 * EPIC-005 + 2026-05-02 demo-parity remap.
 *
 * The API stores PI tier as `bronze | silver | gold | diamond`
 * (the historical enum); the user-visible names + colours flipped
 * to the demo's Cadet / Champion / Elite / Elite Pro palette in
 * PR #798 / #800 / #805. This module is the single source of
 * truth for that remap so /pi/leaderboard, /pi/[username], and
 * /u/[username] don't drift.
 *
 * Colour palette (Tailwind-aligned, dark-theme readable):
 *   Cadet     #94A3B8  (slate-400)
 *   Champion  #F59E0B  (amber-500)
 *   Elite     #A78BFA  (violet-400)
 *   Elite Pro #22D3EE  (cyan-400)
 *
 * BPS / cap values mirror the demo's `PI_TIERS` constants — the
 * /pi/leaderboard hero card uses them; the actual fee-rate config
 * is admin-controlled via /admin/fees (STORY-902).
 */
export interface PiTierMeta {
  key: PiTier;
  /** English literal — for non-i18n consumers / fallback rendering. */
  label: string;
  /** i18n key (`piLeaderboard.tier*`) — pass to `t()` for translated label. */
  labelKey: string;
  color: string;
  bg: string;
  border: string;
  /** Basis points of AUM the PI earns monthly (50 = 0.5%). */
  bps: number;
  /** Monthly cap in USD whole units; `null` = unlimited (Elite Pro). */
  capUsd: number | null;
}

export const PI_TIERS: PiTierMeta[] = [
  {
    key: "bronze",
    label: "Cadet",
    labelKey: "piLeaderboard.tierCadet",
    color: "#94A3B8",
    bg: "color-mix(in srgb, #94A3B8 14%, transparent)",
    border: "#94A3B8",
    bps: 50,
    capUsd: 1000,
  },
  {
    key: "silver",
    label: "Champion",
    labelKey: "piLeaderboard.tierChampion",
    color: "#F59E0B",
    bg: "color-mix(in srgb, #F59E0B 14%, transparent)",
    border: "#F59E0B",
    bps: 100,
    capUsd: 5000,
  },
  {
    key: "gold",
    label: "Elite",
    labelKey: "piLeaderboard.tierElite",
    color: "#A78BFA",
    bg: "color-mix(in srgb, #A78BFA 14%, transparent)",
    border: "#A78BFA",
    bps: 150,
    capUsd: 50000,
  },
  {
    key: "diamond",
    label: "Elite Pro",
    labelKey: "piLeaderboard.tierElitePro",
    color: "#22D3EE",
    bg: "color-mix(in srgb, #22D3EE 14%, transparent)",
    border: "#22D3EE",
    bps: 200,
    capUsd: null,
  },
];

export const PI_TIER_BY_KEY: Record<PiTier, PiTierMeta> = Object.fromEntries(
  PI_TIERS.map((t) => [t.key, t]),
) as Record<PiTier, PiTierMeta>;

/**
 * Resolve a tier (or null — pre-cron PIs render as Cadet) to its
 * display meta. Mirrors the fallback every consumer was already
 * doing inline.
 */
export function piTierMeta(tier: PiTier | null): PiTierMeta {
  return PI_TIER_BY_KEY[tier ?? "bronze"];
}
